'use client';

import { Repeat2 } from 'lucide-react';
import Link from 'next/link';
import { type ComponentType, type HTMLAttributes, useMemo } from 'react';
import { LinkPreview } from '@/components/shared/link-preview/LinkPreview';
import { UserPreview } from '@/components/shared/previews/UserPreview';
import { detectManualRetweet } from '@/lib/detect-manual-retweet';
import { type ParseableTweet, parseTweetText } from '@/lib/tweet-parser';

const MANUAL_RT_TWEET_URL_PATTERN = /https?:\/\/(?:www\.|mobile\.)?(twitter\.com|x\.com)\/[^/\s]+\/status\/(\d+)/i;

interface ManualRetweetProperties extends HTMLAttributes<HTMLElement> {
  InlineTweetComponent: ComponentType<{ tweetId: string; fallbackUrl?: string; visitedIds?: ReadonlySet<string> }>;
  tweet: {
    id: string;
    text: string;
    author: {
      name: string;
      username: string;
    };
    entities?: {
      urls?: Array<{
        url: string;
        expandedUrl: string;
        displayUrl: string;
        title: string | undefined;
        description: string | undefined;
      }>;
      mentions?: Array<{ username: string; id?: string; start?: number; end?: number }>;
      hashtags?: Array<{ tag: string; start?: number; end?: number }>;
    };
  };
}

export function ManualRetweet({ tweet, InlineTweetComponent, ...properties }: ManualRetweetProperties) {
  const manualRtInfo = detectManualRetweet(tweet.text);
  const originalTweetId = useMemo(() => {
    // Try to extract tweet ID if the RT contains a Twitter URL
    const match = manualRtInfo.originalText?.match(MANUAL_RT_TWEET_URL_PATTERN);
    return match?.[2] ?? null;
  }, [manualRtInfo.originalText]);

  // Parse the RT text to extract expanded URLs and format properly
  // Create a pseudo-tweet object with the RT content and original entities if available
  const rtTweetForParsing: ParseableTweet = {
    text: manualRtInfo.originalText ?? '',
  };
  if (tweet.entities) {
    const normalizedEntities: NonNullable<ParseableTweet['entities']> = {};
    if (Array.isArray(tweet.entities.urls) && tweet.entities.urls.length > 0) {
      normalizedEntities.urls = tweet.entities.urls.map(({ title: _ignoredTitle, ...rest }) => ({
        ...rest,
        description: null,
      }));
    }
    if (Array.isArray(tweet.entities.mentions) && tweet.entities.mentions.length > 0) {
      normalizedEntities.mentions = tweet.entities.mentions;
    }
    if (Array.isArray(tweet.entities.hashtags) && tweet.entities.hashtags.length > 0) {
      normalizedEntities.hashtags = tweet.entities.hashtags;
    }
    if (Object.keys(normalizedEntities).length > 0) {
      rtTweetForParsing.entities = normalizedEntities;
    }
  }
  const { html: parsedHtml, cardUrls } = parseTweetText(rtTweetForParsing);

  return (
    <article className="hover:bg-muted/20 transition-colors" {...properties}>
      {/* Retweet indicator */}
      <div className="flex items-center gap-2 px-3 sm:px-4 pt-2 pb-1 text-muted-foreground text-xs sm:text-sm">
        <Repeat2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 ml-6 sm:ml-8" />
        <span className="font-medium">{tweet.author.name} reposted</span>
      </div>

      {/* If we found a tweet ID, try to load the actual tweet */}
      {originalTweetId ? (
        <div className="px-3 sm:px-4 pb-3">
          <InlineTweetComponent
            tweetId={originalTweetId}
            fallbackUrl={`https://twitter.com/i/status/${originalTweetId}`}
            // Seed visited set with the manual RT id so the original tweet cannot loop back here.
            visitedIds={new Set([tweet.id])}
          />
        </div>
      ) : (
        /* Otherwise show the RT content in a styled box */
        <div className="px-3 sm:px-4 pb-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-2">
              {manualRtInfo.originalUsername ? (
                <UserPreview username={manualRtInfo.originalUsername}>
                  <Link href={`/@${manualRtInfo.originalUsername}`} className="text-sm font-semibold hover:underline">
                    @{manualRtInfo.originalUsername}
                  </Link>
                </UserPreview>
              ) : (
                <span className="text-sm font-semibold text-muted-foreground">Unknown author</span>
              )}
            </div>
            <div
              className="w-full min-w-0 max-w-full text-[14px] sm:text-[15px] leading-5 sm:leading-6 whitespace-pre-wrap break-words [word-break:break-word] [overflow-wrap:anywhere] text-foreground"
              dangerouslySetInnerHTML={{ __html: parsedHtml }}
            />

            {/* Show link previews for card URLs */}
            {cardUrls.map((urlInfo) => (
              <LinkPreview key={urlInfo.expandedUrl || urlInfo.url} url={urlInfo.expandedUrl || urlInfo.url} />
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
